import { useState } from "react"
import {
  FEATURE_AVAILABILITY_TYPE_CODE,
  WHATS_NEW_LINK,
  whatsNewQuery,
} from "@/v2-common/featureSurface/featureSurface.constants"
import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import FeatureAccessDemand from "@/v2-backoffice-client/feature/access/FeatureAccessDemand"
import { useFeatureUpdateMutation } from "@/v2-backoffice-client/feature/update/feature.update.mutation"
import { LICENSING_PATH } from "@/v2-backoffice-client/license/license.constants"
import {
  splitPackageNames,
  whoHasPackaged,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import type { FeatureFragment } from "@/v2-backoffice-client/feature/__types__/feature.fragment"

export type FeatureAccessSectionProps = {
  className?: string
  feature: FeatureFragment
  /** Surface segment of this section's feature codes, e.g. "Basemaps". */
  featureCodeLabel: string
}

/**
 * Who may reach a feature at all, before country availability narrows it
 * further: who has it today, read-only, with the way to the one board that
 * decides it; and whether it counts as personal data, saved here.
 *
 * Who has a feature is a licensing decision with one home, Licensing >
 * Feature access (the Capabilities view for a console surface, the Content view
 * for a map layer or base map), where the lock's blast radius is shown and
 * the carrying packages are written in the same save. This section used to
 * offer the same column as a plain select, in every environment, and that
 * second door is how 32 map layers went package-only with no package behind
 * them (FUS-2787); it now only says what is decided and where.
 *
 * The personal-data flag saves to the running environment immediately (and
 * mirrors to the authoring catalog), unlike the text on the same row, which
 * waits for a publish.
 */
function FeatureAccessSection(props: FeatureAccessSectionProps) {
  const { feature, featureCodeLabel, className } = props

  // The saved row is the baseline; `draft` holds the operator's unsaved edit
  // on top of it and is dropped whenever the row underneath changes.
  const [draft, setDraft] = useState<boolean | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const currentKey = `${feature.Code}:${feature.IsPersonalData}`
  if (currentKey !== savedKey) {
    setSavedKey(currentKey)
    setDraft(null)
  }
  const isPersonalData = draft ?? feature.IsPersonalData

  const [save, { loading: isSaving, error }] = useFeatureUpdateMutation({
    onCompleted() {
      setDraft(null)
      toast({ title: "Saved to the live runtime." })
    },
  })
  errorToast(error, { title: "Failed to save" })

  const isLocked =
    feature.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  const packages = splitPackageNames(feature.FeatureGroupNames)
  const decidedOn = feature.IsCapability
    ? WHATS_NEW_LINK.viewCapabilities
    : feature.IsContent
      ? WHATS_NEW_LINK.viewContent
      : null

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="space-y-1">
        <Label>Who has it</Label>
        <p className="text-sm">{whoHasPackaged(isLocked, packages)}</p>
        {decidedOn ? (
          <p className="text-xs text-muted-foreground">
            Decided on Licensing → Feature access, where who would lose it is
            shown before a change.{" "}
            <Link
              variant="muted"
              size="xs"
              iconSrc={null}
              className="underline underline-offset-2"
              to={`${LICENSING_PATH}?${whatsNewQuery(feature.Code, decidedOn)}`}
              data-feature-code={`UI_Backoffice${featureCodeLabel}_Access_WhatsNew`}
            >
              {isLocked
                ? "Change the packages or open it"
                : "Decide who has it"}
            </Link>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Nothing checks this feature against a license, so it is
            everyone&apos;s; there is nothing to decide.
          </p>
        )}
      </div>

      {/* The demand behind the decision above (AIP-47): who tried to use
          this while locked. Quiet for a free feature nobody was refused. */}
      {decidedOn && (
        <FeatureAccessDemand featureCode={feature.Code} isLocked={isLocked} />
      )}

      <div className="flex items-start gap-3">
        <Label className="flex flex-1 items-start gap-2 font-normal">
          <Checkbox
            checked={isPersonalData}
            disabled={isSaving}
            onCheckedChange={(isChecked) => {
              setDraft(isChecked === true)
            }}
          />
          <span>
            Personal data
            <span className="block text-xs text-muted-foreground">
              Subject to the private-data visibility policies: what it shows is
              withheld from users the policies do not clear.
            </span>
          </span>
        </Label>
        <Button
          size="sm"
          isLoading={isSaving}
          disabled={draft === null || draft === feature.IsPersonalData}
          data-feature-code={`UI_Backoffice${featureCodeLabel}_Access_Save`}
          onClick={() => {
            void save({
              variables: {
                input: {
                  FeatureCode: feature.Code,
                  IsPersonalData: isPersonalData,
                },
              },
            })
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

export default FeatureAccessSection
